export type SparkKind = "dot" | "streak";

export type Spark = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  kind: SparkKind;
  len: number;
  color?: string;
};

export type GlitterTier = "low" | "steady" | "good" | "breakthrough";
export type GlitterColors = { core: string; bloom: string; particles: readonly string[] };

type Rng = () => number;

function parseHex(hex: string): [number, number, number] {
  const raw = hex.replace("#", "");
  return [
    Number.parseInt(raw.slice(0, 2), 16),
    Number.parseInt(raw.slice(2, 4), 16),
    Number.parseInt(raw.slice(4, 6), 16),
  ];
}

function toHex(value: number): string {
  return Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0");
}

export function mixTowardWhite(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

export function glitterTierForGain(gain: number): GlitterTier {
  if (gain <= 2) return "low";
  if (gain <= 4) return "steady";
  if (gain <= 9) return "good";
  return "breakthrough";
}

export function glitterColors(progressHex: string, tier: GlitterTier): GlitterColors {
  const bloom = progressHex.startsWith("#") ? progressHex : "#2db89a";
  const mint = mixTowardWhite(bloom, 0.78);
  if (tier === "low") return { core: mixTowardWhite(bloom, 0.46), bloom, particles: [bloom] };
  if (tier === "steady") return { core: mint, bloom, particles: [bloom, mint] };
  if (tier === "good") return { core: mint, bloom, particles: [bloom, mint, "#ffe192"] };
  return {
    core: "#ffffff",
    bloom,
    particles: ["#ff7b8d", "#ffcf5c", "#82df8a", "#70d9ff", "#9e8cff", "#ff91cf"],
  };
}

function rand(min: number, max: number, rng: Rng): number {
  return min + rng() * (max - min);
}

function pickColor(colors: readonly string[], rng: Rng): string {
  return colors[Math.floor(rng() * colors.length)] ?? "#ffffff";
}

function spawnDot(x: number, y: number, colors: readonly string[], rng: Rng): Spark {
  return {
    x,
    y: y + rand(-2, 2, rng),
    vx: rand(-42, -8, rng),
    vy: rand(-70, 70, rng),
    life: 0,
    maxLife: rand(0.18, 0.42, rng),
    size: rand(0.7, 2.1, rng),
    kind: "dot",
    len: 0,
    color: pickColor(colors, rng),
  };
}

function spawnStreak(x: number, y: number, colors: readonly string[], rng: Rng): Spark {
  return {
    x: x + rand(-3, 2, rng),
    y,
    vx: rand(-18, 6, rng),
    vy: 0,
    life: 0,
    maxLife: rand(0.05, 0.14, rng),
    size: rand(0.6, 1.4, rng),
    kind: "streak",
    len: rand(10, 54, rng),
    color: pickColor(colors, rng),
  };
}

export function spawnBurst(
  x: number,
  y: number,
  speed: number,
  intense: boolean,
  rng: Rng = Math.random,
  colors: readonly string[] = ["#ffffff"],
): Spark[] {
  const boost = intense ? 1.6 : 1;
  const n = Math.min(22, Math.ceil((3 + Math.floor(speed * 90)) * boost));
  const streaks = Math.min(10, Math.ceil((1 + Math.floor(speed * 40)) * boost));
  const out: Spark[] = [];
  for (let i = 0; i < n; i += 1) out.push(spawnDot(x, y, colors, rng));
  for (let i = 0; i < streaks; i += 1) out.push(spawnStreak(x, y, colors, rng));
  return out;
}

export function stepSparks(list: Spark[], dt: number): Spark[] {
  const next: Spark[] = [];
  for (const spark of list) {
    const life = spark.life + dt;
    if (life >= spark.maxLife) continue;
    next.push({
      ...spark,
      life,
      x: spark.x + spark.vx * dt,
      y: spark.y + spark.vy * dt,
      vy: spark.vy * (1 - 2.4 * dt),
    });
  }
  return next;
}
